// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rails that will fuse into a board instead of staying separate.
 *
 * The panel substrate is a `unary_union` of every piece (`Substrate.union`), so
 * anything that overlaps or merely touches becomes one polygon — and a rail
 * fused to a board has no cut between them, which means the board cannot be
 * broken out. On a screen at panel zoom a 0.05 mm gap and a 0 mm gap look
 * identical, so this is easy to do by accident and impossible to see.
 *
 * Rail-to-rail overlap is deliberately *not* reported: fusing a backbone into a
 * frame is the normal way to make a panel rigid, and `example_panel.yaml` does
 * exactly that on purpose.
 */

import type { BBox } from '../model/bbox.ts';
import type { Point } from '../model/geometry.ts';
import { type Polygon, pointInPolygon, ringsOf } from './outline.ts';

/**
 * Below this gap, in mm, the two pieces end up as one.
 *
 * KiKit buffers each board outline outward by 0.001 mm when appending it and
 * simplifies substrates with a 0.001 mm tolerance, so roughly 0.004 mm of gap
 * can close on its own. A factor of ten over that is the point past which the
 * answer stops depending on rounding.
 */
export const MERGE_GAP = 0.04;

/**
 * Gaps below this are reported as risky even though they will not strictly
 * fuse: a sliver this thin is not a gap anyone can cut along.
 */
export const TIGHT_GAP = 0.15;

export interface ClearanceIssue {
  railIndex: number;
  boardIndex: number;
  boardName: string;
  /** 0 when the two overlap or touch. */
  gap: number;
  severity: 'merge' | 'tight';
  message: string;
}

function segmentDistance(a: Point, b: Point, c: Point, d: Point): number {
  // Touching or crossing segments are at zero distance.
  if (segmentsIntersect(a, b, c, d)) return 0;
  return Math.min(
    pointToSegment(a, c, d),
    pointToSegment(b, c, d),
    pointToSegment(c, a, b),
    pointToSegment(d, a, b),
  );
}

function cross(o: Point, a: Point, b: Point): number {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

function segmentsIntersect(a: Point, b: Point, c: Point, d: Point): boolean {
  const d1 = cross(c, d, a);
  const d2 = cross(c, d, b);
  const d3 = cross(a, b, c);
  const d4 = cross(a, b, d);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }
  return false;
}

function pointToSegment(p: Point, a: Point, b: Point): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

function boxGap(a: BBox, b: BBox): number {
  const dx = Math.max(0, Math.max(a.minX - b.maxX, b.minX - a.maxX));
  const dy = Math.max(0, Math.max(a.minY - b.maxY, b.minY - a.maxY));
  return Math.hypot(dx, dy);
}

function ringBox(ring: ReadonlyArray<Point>): BBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Closest approach between two polygons; 0 when they touch or overlap.
 *
 * Stops as soon as it finds a zero, and skips ring pairs whose boxes are
 * already further apart than the best distance so far — a board outline can
 * carry hundreds of vertices and this runs on every preview.
 */
export function polygonGap(a: Polygon, b: Polygon, limit = Infinity): number {
  // Containment is a zero gap that no edge comparison would find.
  if (pointInPolygon(a, b.ext[0]![0], b.ext[0]![1])) return 0;
  if (pointInPolygon(b, a.ext[0]![0], a.ext[0]![1])) return 0;

  let best = limit;
  for (const ringA of ringsOf(a)) {
    const boxA = ringBox(ringA);
    for (const ringB of ringsOf(b)) {
      if (boxGap(boxA, ringBox(ringB)) >= best) continue;
      for (let i = 0; i < ringA.length; i++) {
        const a0 = ringA[i]!;
        const a1 = ringA[(i + 1) % ringA.length]!;
        for (let j = 0; j < ringB.length; j++) {
          const d = segmentDistance(a0, a1, ringB[j]!, ringB[(j + 1) % ringB.length]!);
          if (d < best) {
            best = d;
            if (best === 0) return 0;
          }
        }
      }
    }
  }
  return best;
}

export interface ClearanceInput {
  rails: Array<{ index: number; polys: Polygon[] }>;
  boards: Array<{ index: number; name: string; polys: Polygon[] }>;
}

/** Every rail that will fuse with a board, or come close enough to worry. */
export function checkClearances(input: ClearanceInput): ClearanceIssue[] {
  const issues: ClearanceIssue[] = [];

  for (const rail of input.rails) {
    for (const board of input.boards) {
      let gap = Infinity;
      for (const railPoly of rail.polys) {
        for (const boardPoly of board.polys) {
          gap = Math.min(gap, polygonGap(railPoly, boardPoly, gap));
          if (gap === 0) break;
        }
        if (gap === 0) break;
      }
      if (gap > TIGHT_GAP) continue;

      const where = `rails[${rail.index}] and board '${board.name}'`;
      issues.push(
        gap <= MERGE_GAP
          ? {
              railIndex: rail.index,
              boardIndex: board.index,
              boardName: board.name,
              gap,
              severity: 'merge',
              message:
                gap === 0
                  ? `${where} touch or overlap: they will fuse into one piece, with no cut between them`
                  : `${where} are only ${gap.toFixed(4)} mm apart: KiKit's geometry tolerance will fuse them`,
            }
          : {
              railIndex: rail.index,
              boardIndex: board.index,
              boardName: board.name,
              gap,
              severity: 'tight',
              message: `${where} are only ${gap.toFixed(3)} mm apart: too thin to depanel cleanly`,
            },
      );
    }
  }

  return issues;
}
